import { Component, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { MarkdownComponent } from 'src/app/components/markdown/markdown.component';
import { PendingReply } from 'src/app/services/agent-chat/agent-chat-store.service';
import { AgentFeedbackRating, AgentMessage } from 'src/app/types/agent-chat';

/** Long enough for a few sentences on what was wrong; the API's own cap. */
export const AGENT_FEEDBACK_COMMENT_MAX_LENGTH = 1000;

export interface AgentMessageRating {
  messageId: string;
  rating: AgentFeedbackRating;
  comment?: string;
}

/**
 * A support conversation's messages: the merchant's, the agent's (markdown) and the Goodchart
 * team's. The merchant chat (GOO-457) shows it with the reply still streaming as `pending` and lets
 * the merchant rate the agent's replies (GOO-458); the admin Support page (GOO-467) reuses it with
 * `viewer` set to `admin`, where ratings are read only.
 */
@Component({
  selector: 'app-agent-message-list',
  standalone: true,
  imports: [FormsModule, MatButtonModule, MatIconModule, MarkdownComponent, TranslatePipe],
  templateUrl: './agent-message-list.component.html',
})
export class AgentMessageListComponent {
  readonly messages = input<AgentMessage[]>([]);
  readonly pending = input<PendingReply | null>(null);
  /** Whose screen it is: the merchant's own messages read as "You"; on the admin page, "Merchant". */
  readonly viewer = input<'merchant' | 'admin'>('merchant');
  readonly rated = output<AgentMessageRating>();

  /** The reply the merchant is explaining a thumbs down for, and what they've written so far. */
  readonly commenting = signal<string | null>(null);
  readonly comment = signal('');

  readonly commentMaxLength = AGENT_FEEDBACK_COMMENT_MAX_LENGTH;

  rate(messageId: string, rating: AgentFeedbackRating) {
    this.rated.emit({ messageId, rating });
    this.comment.set('');
    this.commenting.set(rating === 'down' ? messageId : null);
  }

  sendComment(messageId: string) {
    const comment = this.comment().trim();

    if (comment) this.rated.emit({ messageId, rating: 'down', comment });

    this.commenting.set(null);
  }
}
